/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { propsUtils, keyboardUtils, domUtils } from '@ba-ui-toolkit/utils';

import checkmarkIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/checkmark--filled_24.js';
import closeIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16.js';
import infoIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/information_32.js';
import errorIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/warning_16.js';
import warnIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/warning--alt_32.js';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import ToolTip from '@ba-ui-toolkit/tooltip';
import Button from '@ba-ui-toolkit/button';
import styles from './globaltoast.scss';
import stringResource from './nls/StringResource';

const globalToastClassName = styles.globalToastClassName.replace(/"/g, '');
const { sanitize } = propsUtils;
const { isKey, keyNames } = keyboardUtils;
const { enableFocusLoop, disableFocusLoop } = domUtils;

function createIcon(icon, size, statusText, intent, className) {
	if (!icon) {
		return null;
	}
	const iconComponent =
		typeof icon === 'string' ? (
			<SVGIcon
				data-tid="ba-common-global-toast__status-icon"
				aria-label={statusText}
				className={className}
				size={size}
				intent={intent}
				focusable={false}
				iconId={icon}
			/>
		) : (
			icon
		);

	return iconComponent;
}

function getStatusIconText(statusType, iconText) {
	if (iconText == null) {
		switch (statusType) {
			case 'info':
				return stringResource.get('information');
			case 'success':
				return stringResource.get('success');
			case 'warning':
				return stringResource.get('warning');
			case 'error':
				return stringResource.get('error');
			default:
				return iconText;
		}
	}

	return iconText;
}

function createAlertIcon(statusType, statusText) {
	switch (statusType) {
		case 'info':
			return createIcon(`${infoIcon.id}`, 'normal', statusText, undefined, 'color-g4');
		case 'success':
			return createIcon(`${checkmarkIcon.id}`, 'normal', statusText, 'positive');
		case 'warning':
			return createIcon(`${warnIcon.id}`, 'normal', statusText, 'warning');
		case 'error':
			return createIcon(`${errorIcon.id}`, 'normal', statusText, 'danger');
		default:
			return null;
	}
}

export default class GlobalToast extends Component {
	constructor(props) {
		super(props);
		this.timerId = null;
	}

	static propTypes = {
		statusType: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
		children: PropTypes.node,
		contentString: PropTypes.string,
		onClose: PropTypes.func,
		closeText: PropTypes.string,
		statusIconText: PropTypes.string,
		timedDismiss: PropTypes.bool,
		milliseconds: PropTypes.number,
		className: PropTypes.string,
		style: PropTypes.object,
		startingFocusIndex: PropTypes.number,
		targetId: PropTypes.string,
		stackable: PropTypes.bool,
	};

	static defaultProps = {
		statusType: 'success',
		contentString: '',
		timedDismiss: false,
		milliseconds: 4000,
		closeText: stringResource.get('close'),
		statusIconText: null,
		className: '',
		style: {},
		startingFocusIndex: -1,
		targetId: '',
		stackable: false,
	};

	state = {
		touch: false,
	};

	componentDidMount() {
		enableFocusLoop(this.blockerCell, this.props.startingFocusIndex);
		this._setTimeoutId();
		window.addEventListener('touchstart', this._detectTouch, false);
	}

	_detectTouch = () => {
		this.setState({ touch: true });
		window.removeEventListener('touchstart', this._detectTouch, false);
	};

	componentWillUpdate() {
		this._setTimeoutId();
	}

	componentWillUnmount() {
		disableFocusLoop(this.blockerCell);
		clearTimeout(this.timerId);
	}

	_onClose = e => {
		const { onClose } = this.props;
		onClose && onClose(e);
		clearTimeout(this.timerId);
	};

	_onKeyDown = e => {
		if (isKey(e, keyNames.ESCAPE)) this._onClose(e);
	};

	_setTimeoutId = () => {
		const { timedDismiss, milliseconds } = this.props;
		if (timedDismiss === true) {
			this.timerId = window.setTimeout(() => {
				this._onClose();
			}, milliseconds);
		}
	};

	_blockerCellRef = node => {
		this.blockerCell = node;
	};

	_createNode = () => {
        const { statusType, contentString, children, closeText, className, style, statusIconText, timedDismiss, targetId, stackable, ...others } = this.props;
        const statusText = getStatusIconText(statusType, statusIconText);
        const AlertIcon = createAlertIcon(statusType, statusText);
        const closeTooltip = closeText;
        const closeA11y = timedDismiss === true ? '' : closeText;
        const targeted = targetId ? ' is-targeted' : '';
        const foundTarget = (document.getElementById(targetId) && targetId) || !targetId ? '' : ' is-not-found';

        return (
            <div {...sanitize(others, GlobalToast)} role="alert" className={`${globalToastClassName} is-${statusType} ${className}${targeted}${foundTarget} ${stackable ? 'is-stackable' : ''}`} style={style}>
                <div className={`${globalToastClassName}__modal`} ref={this._blockerCellRef} onKeyDown={this._onKeyDown}>
                    <ToolTip
                        className={`${globalToastClassName}__statusIcon`}
                        orient='top'
                        title={statusText}
                    >
                        {AlertIcon || ''}
                    </ToolTip>
                    <div
                        className={`${globalToastClassName}__content`}
                        data-tid='ba-common-global-toast__content'
                    >{contentString || children}</div>
                    <ToolTip
                        className={`${globalToastClassName}__closeX color-g4`}
                        orient='top'
                        title={closeTooltip}
                        disabled={this.state.touch}
                    >
                        <Button
                            data-tid='ba-common-global-toast__close-x'
                            icon={closeIcon.id}
                            onClick={this._onClose}
                            iconSize="small"
                            variant="icon"
                            intent="primary"
                            aria-label={closeA11y}
                            aria-hidden={timedDismiss}
                        />
                    </ToolTip>
                </div>
            </div>
        );
    }

    render() {
        const { targetId, stackable } = this.props;
        return (
            stackable ? this._createNode() : createPortal(
                this._createNode(),
                document.getElementById(targetId) || document.body,
            )
        );
    }
}
